// src/views/Settings.jsx
// Extracted from App.jsx — behavior-preserving split (2026-07-20).
// 2026-10-01: two staff logins. Rate-sheet extraction keeps every PLU band
// (and never touches the market rate unless asked); the password panel
// changes the SIGNED-IN user's real Supabase Auth password.
// 2026-10-01 (later): Max is limited for now (src/access.js). His Settings is
// a short page of its own — LimitedSettings below: change my password and the
// Practice mode switch, stored on HIS login (Supabase Auth user_metadata),
// never in the shared settings row (which he can only read). No LO split for
// Max at all (Reece, 10/1) — the split box is Reece's alone.
// Everything else on this page is Reece's.

import React, { useState, useEffect, useMemo, useRef } from "react";
import { supabase, isSupabaseReady, STAFF } from '../supabase';
import { extractRateSheet, mapCategoryToAppKey, encodeForAppKey, assessRateSheetChange, MAX_RATE_SHEET_BYTES, MAX_RATE_SHEET_LABEL } from '../rateSheetExtractor';
import { addRateSheetToLibrary, rateSheetLibraryNote } from '../kb';
import * as RC from '../ringcentral';
import { C, Ic } from '../theme';
import { Badge, Btn, Hdr, Inp, MoneyInp } from '../ui';
import { ALLOW_DATA_WIPE, LS_KEY } from '../constants';
import { hashPassword, isLegacyPassword, pushSwapMark, swapHistory, parseDateLocal } from '../lib';
import { staffLoSplit } from './Expenses';
import { staffAccess, useStaffPrefs, isLimitedStaff } from '../access';
import { ownershipUiOn, withOwnershipUi } from '../features';

// HUD's national limit has never been outside this band; a typo (an extra
// digit, a dropped one) lands far outside it and would un-cap every HECM.
const HECM_LIMIT_MIN=500000,HECM_LIMIT_MAX=3000000;
const withCommas=v=>String(v==null?"":v).replace(/[^0-9]/g,"").replace(/\B(?=(\d{3})+(?!\d))/g,",");

// Today's Market is ONE rate: a change writes every sheet that carries the
// field and stamps the swap history — the same thing Calc's MarketBar does
// (setMarketRate). The rate-sheet extractor only moves it through here, and
// only when he ticks the box.
function applyMarketRate(rs,field,val){
  var next=Object.assign({},rs);
  Object.keys(next).forEach(function(k){
    if(k.charAt(0)==="_"||k==="incentives")return;
    if(next[k]&&typeof next[k]==="object"&&next[k][field]!==undefined){var o={};o[field]=val;next[k]=Object.assign({},next[k],o);}
  });
  if(field==="swapRate")next._swapHistory=pushSwapMark(rs,val,(next.foaHecmAdj||{}).index);
  return next;
}
const marketVal=v=>{const n=parseFloat(v);return(isNaN(n)||n<=0||n>25)?null:String(n);};

// Merge a backup's leads into the current list by id: append mailings and
// history entries that aren't there yet, insert leads that don't exist.
// Never overwrites name/address/phone/status on a matched lead. Used for the
// preview AND the apply, so the success message reports what really merged.
function restoreMerge(fileLeads,curLeads,wantMerged){
  const byId=new Map();(curLeads||[]).forEach(l=>{if(l&&l.id)byId.set(l.id,l);});
  let mailingsAdded=0,historyAdded=0,leadsTouched=0,newLeads=0;
  const updates=new Map();const toAdd=[];const seenNew=new Set();
  (fileLeads||[]).forEach(fl=>{
    if(!fl||!fl.id)return;
    const cur=byId.get(fl.id);
    if(cur){
      const base=updates.get(fl.id)||cur;
      const curMailIds=new Set((base.mailings||[]).map(m=>m&&m.id));
      const newMs=(fl.mailings||[]).filter(m=>m&&m.id&&!curMailIds.has(m.id));
      const curHistIds=new Set((base.history||[]).map(h=>h&&h.mailingId).filter(Boolean));
      const newHs=(fl.history||[]).filter(h=>h&&h.mailingId&&!curHistIds.has(h.mailingId));
      if(!newMs.length&&!newHs.length)return;
      if(!updates.has(fl.id))leadsTouched++;
      mailingsAdded+=newMs.length;historyAdded+=newHs.length;
      const mergedMailings=[...(base.mailings||[]),...newMs];
      const wasNew=base.status==="New"&&mergedMailings.length>0;
      updates.set(fl.id,{...base,mailings:mergedMailings,history:[...(base.history||[]),...newHs],status:wasNew?"Mailed":base.status});
    }else if(!seenNew.has(fl.id)){seenNew.add(fl.id);newLeads++;toAdd.push(fl);}
  });
  const counts={mailingsAdded,historyAdded,leadsTouched,newLeads};
  if(!wantMerged)return{counts};
  return{counts,merged:[...(curLeads||[]).map(l=>(l&&updates.get(l.id))||l),...toAdd]};
}

function FullSettings({staff,allLeadsLoaded,loadAllLeads,settings,updateSettings,smsLog,loans,setLoans,borrowerAccts,setBorrowerAccts,mktgLeads,setMktgLeads,leadsReady,campaigns,setCampaigns,expenses,setExpenses,setSmsLog,rcAuthed,setRcAuthed,rcStatus}){
  const who=staff==="max"?"max":"reece";
  const isReece=who==="reece";
  const me=(STAFF&&STAFF[who])||{name:who==="max"?"Max":"Reece"};
  const [saved,sSaved]=useState(false);
  // Per-device click-to-call launcher (stored in this browser only).
  const [callMode,sCallMode]=useState(function(){try{return RC.getCallLaunchMode();}catch(e){return"auto";}});
  const [newAdminPw,sNewAdminPw]=useState("");
  const [confirmAdminPw,sConfirmAdminPw]=useState("");
  const [pwErr,sPwErr]=useState("");
  const [pwOk,sPwOk]=useState("");
  const [pwBusy,sPwBusy]=useState(false);
  const [newDmCamp,sNewDmCamp]=useState("");
  const mySplit=staffLoSplit(settings,who);
  const [loSplitInp,sLoSplitInp]=useState(String(mySplit));
  const [hecmLimitInp,sHecmLimitInp]=useState(String(settings.hecmLimit||""));
  const [hecmErr,sHecmErr]=useState("");
  const [adminPhoneInp,sAdminPhoneInp]=useState(settings.adminPhone||"");
  const [rcClientIdInp,sRcClientIdInp]=useState(settings.rcClientId||"");
  const [rcServerInp,sRcServerInp]=useState(settings.rcServerUrl||"");
  const [rcSmsFromInp,sRcSmsFromInp]=useState(settings.rcSmsFrom||"");
  // Settings can load (or change on the other user's save) after this view
  // mounted — keep the inputs on the saved value until he starts typing.
  useEffect(()=>{sLoSplitInp(String(mySplit));},[mySplit]);
  useEffect(()=>{sHecmLimitInp(String(settings.hecmLimit||""));},[settings.hecmLimit]);

  // ── Restore from JSON backup ──────────────────────────────
  // Accepts either the leads-only auto-backup written after every bulk
  // mail-log op OR the full Settings-page backup. Only mailed/worked leads
  // load at login, so the preview waits for the WHOLE table before it counts
  // anything — otherwise every never-mailed lead reads as "new" and the
  // restore would roll live rows back to the backup's older copy.
  //   - match existing leads by id
  //   - de-dupe mailings by mailing id, history by mailingId
  //   - never overwrite name/address/phone/status on matched leads
  //   - leads in the file with no current match are inserted as new
  const [restoreFile,sRestoreFile]=useState(null);
  const [restoreError,sRestoreError]=useState("");
  const [restoreDone,sRestoreDone]=useState("");
  const [restoreLoading,sRestoreLoading]=useState(false);
  const restoreLoadRef=useRef(false);
  const needAllLeads=leadsReady===false||allLeadsLoaded===false;

  const onPickRestoreBackup=(e)=>{
    sRestoreError("");sRestoreFile(null);sRestoreDone("");
    const f=e.target.files&&e.target.files[0];if(!f)return;
    const rd=new FileReader();
    rd.onload=(ev)=>{
      try{
        const data=JSON.parse(String(ev.target.result||""));
        const fileLeads=Array.isArray(data.mktgLeads)?data.mktgLeads:null;
        if(!fileLeads){sRestoreError("This JSON doesn't contain a mktgLeads array. Make sure you picked a LoanFlow backup file.");return;}
        sRestoreFile({fileLeads,fileCount:fileLeads.length,exportedAt:data._meta&&data._meta.exportedAt,label:data._meta&&data._meta.label,fileName:f.name});
      }catch(err){sRestoreError("Couldn't parse JSON: "+err.message);}
    };
    rd.onerror=()=>sRestoreError("Could not read the file.");
    rd.readAsText(f);
    e.target.value="";
  };
  // A backup is open but the full lead table isn't here yet → fetch it now.
  useEffect(()=>{
    if(!restoreFile||!needAllLeads||!loadAllLeads||restoreLoadRef.current)return;
    restoreLoadRef.current=true;sRestoreLoading(true);sRestoreError("");
    Promise.resolve(loadAllLeads()).then(ok=>{
      if(ok===false)sRestoreError("Couldn't load the full prospect list. Check your connection and try again.");
    }).catch(err=>sRestoreError("Couldn't load the full prospect list: "+((err&&err.message)||err)))
      .then(()=>{restoreLoadRef.current=false;sRestoreLoading(false);});
  },[restoreFile,needAllLeads,loadAllLeads]);
  const restorePreview=useMemo(()=>{
    if(!restoreFile||needAllLeads)return null;
    return Object.assign({},restoreFile,restoreMerge(restoreFile.fileLeads,mktgLeads,false).counts);
  },[restoreFile,needAllLeads,mktgLeads]);
  const closeRestore=()=>{sRestoreFile(null);sRestoreError("");};

  const applyRestore=()=>{
    if(!restoreFile)return;
    if(needAllLeads){sRestoreError("Still loading the full prospect list — restoring now would overwrite live prospects with older copies. It will be ready in a moment.");return;}
    const {fileLeads}=restoreFile;
    // Counts come from the same merge the update runs, against the same list.
    const {counts}=restoreMerge(fileLeads,mktgLeads,false);
    setMktgLeads(prev=>restoreMerge(fileLeads,prev,true).merged);
    sRestoreDone("Restored "+counts.mailingsAdded+" mailing"+(counts.mailingsAdded!==1?"s":"")+(counts.historyAdded?", "+counts.historyAdded+" history entr"+(counts.historyAdded!==1?"ies":"y"):"")+" and "+counts.newLeads+" new lead"+(counts.newLeads!==1?"s":"")+". The cloud sync will run in a few seconds.");
    sRestoreFile(null);sRestoreError("");
    setTimeout(()=>sRestoreDone(""),8000);
  };

  // ── Rate sheet PDF upload ──────────────────────────────────
  const [rsFileName,sRsFileName]=useState("");
  const [rsDataUrl,sRsDataUrl]=useState(null);
  // The picked PDF itself (the extractor reads the data URL above): applying
  // the sheet also adds this file to the Lender Library.
  const [rsFile,sRsFile]=useState(null);
  // That library add's status line: {state: working|ok|warn|error, text}.
  const [rsLib,sRsLib]=useState(null);
  const [rsLoading,sRsLoading]=useState(false);
  const [rsError,sRsError]=useState("");
  // `rsPreview` holds the full extraction result plus the per-row "apply"
  // state. Each row is { appKey, field, currentValue, newValue, editedValue,
  // bands, warnings, changed, apply } so the user can toggle, edit, and see
  // what they're committing.
  const [rsPreview,sRsPreview]=useState(null);
  // Opt-in: also move Today's Market to the sheet's 1-yr CMT / 10-yr.
  const [rsSetMarket,sRsSetMarket]=useState(false);

  const onPickRateSheet=function(e){
    sRsError("");sRsPreview(null);
    var file=e.target.files&&e.target.files[0];if(!file)return;
    e.target.value="";
    if(!file.type.includes("pdf")){sRsError("Please choose a PDF file.");return;}
    if(file.size>MAX_RATE_SHEET_BYTES){sRsError("This PDF is "+(file.size/1048576).toFixed(1)+"MB — the extractor can only send up to "+MAX_RATE_SHEET_LABEL+". Save just the rate pages as a smaller PDF and try again.");return;}
    sRsFileName(file.name);sRsFile(file);
    sRsLib(function(s){return s&&s.state==="working"?s:null;});
    var reader=new FileReader();
    reader.onload=function(ev){sRsDataUrl(ev.target.result);};
    reader.onerror=function(){sRsError("Could not read the file.");};
    reader.readAsDataURL(file);
  };

  const runRateSheetExtraction=async function(){
    if(!rsDataUrl)return;
    sRsLoading(true);sRsError("");sRsPreview(null);sRsSetMarket(false);
    try{
      var session=isSupabaseReady()?(await supabase.auth.getSession()).data.session:null;
      if(!session){sRsError("Sign in as admin to extract rate sheets.");sRsLoading(false);return;}
      var res=await extractRateSheet(rsDataUrl,session.access_token);
      if(res.error){sRsError(res.error);sRsLoading(false);return;}
      // Build per-product preview rows. Map each extracted product to an
      // app rate-sheet key and encode it for the current schema. Keep
      // unmatched products in a separate bucket so the user sees what
      // needs hand-wiring.
      var rows=[];var unmatched=[];
      (res.extraction.products||[]).forEach(function(p){
        var appKey=mapCategoryToAppKey(p.category,res.extraction.lender,p.life_cap);
        if(!appKey){unmatched.push(p);return;}
        var current=(settings.rateSheets||{})[appKey]||{};
        var encoded=encodeForAppKey(appKey,p).data||{};
        // Which field this product encodes to. svcMargins/svcTiers are the
        // SmartFi Choice Service (veterans-only) tables — same appKey as the
        // regular product but a separate field, so they never overwrite the
        // civilian rates.
        var field=["margins","tiers","svcMargins","svcTiers"].find(function(f){return encoded[f]!==undefined;})||"tiers";
        var newValue=encoded[field]||"";
        var currentValue=current[field]||"";
        var a=assessRateSheetChange(currentValue,newValue,current.bands,encoded.bands);
        rows.push({
          appKey:appKey,
          label:(current.label||p.product_name||appKey)+(field==="svcMargins"||field==="svcTiers"?" — Service (veterans)":""),
          field:field,
          currentValue:currentValue,
          newValue:newValue,
          editedValue:newValue,
          currentBands:current.bands||"",
          newBands:encoded.bands||"",
          bands:a.bands,
          warnings:a.warnings,
          changed:a.changed,
          note:p.special_notes||current.note||"",
          // Pre-checked only when it really changed AND nothing was lost.
          apply:newValue!==""&&a.changed&&a.warnings.length===0,
          rawProduct:p,
        });
      });
      sRsPreview({
        extraction:res.extraction,
        rows:rows,
        unmatched:unmatched,
      });
    }catch(err){
      sRsError("Extraction error: "+(err&&err.message?err.message:String(err)));
    }
    sRsLoading(false);
  };

  const rsCur=(settings.rateSheets||{}).foaHecmAdj||{};
  const pdfSwap=rsPreview?marketVal(rsPreview.extraction.index_10yr):null;
  const pdfCmt=rsPreview?marketVal(rsPreview.extraction.index_1yr_cmt):null;
  const marketDiffers=(pdfSwap!=null&&Math.abs(parseFloat(pdfSwap)-parseFloat(rsCur.swapRate))>=0.0005)||(pdfCmt!=null&&Math.abs(parseFloat(pdfCmt)-parseFloat(rsCur.index))>=0.0005)||(pdfSwap!=null&&isNaN(parseFloat(rsCur.swapRate)));
  const lastMark=swapHistory(settings.rateSheets).slice(-1)[0]||null;
  const sheetDate=rsPreview?parseDateLocal(rsPreview.extraction.effective_date):null;
  const sheetNewerThanMarket=!!(sheetDate&&(!lastMark||sheetDate.getTime()>new Date(lastMark.at||lastMark.epoch||0).getTime()));

  const applyRateSheetPreview=function(){
    if(!rsPreview)return;
    var today=new Date();
    var stamp=(today.getMonth()+1).toString().padStart(2,"0")+"/"+today.getDate().toString().padStart(2,"0")+"/"+String(today.getFullYear()).slice(-2);
    var next=Object.assign({},settings.rateSheets||{});
    var applied=0;
    rsPreview.rows.forEach(function(r){
      if(!r.apply||!r.appKey)return;
      var prev=next[r.appKey]||{};
      var patch={updated:stamp};
      patch[r.field]=r.editedValue;
      // Per-band prices need their band edges; write them only when the
      // sheet's own headers were read and line up with the price columns.
      if(r.bands)patch.bands=r.bands;
      // index / swapRate are NOT written per sheet: lenders don't each have
      // their own market rate, and a per-sheet write split the lenders onto
      // different swaps with no history mark. That goes through Today's
      // Market below, only when ticked.
      next[r.appKey]=Object.assign({},prev,patch);
      applied++;
    });
    var marketMoved=false;
    if(rsSetMarket&&(pdfSwap!=null||pdfCmt!=null)){
      if(pdfCmt!=null)next=applyMarketRate(next,"index",pdfCmt);
      if(pdfSwap!=null)next=applyMarketRate(next,"swapRate",pdfSwap);
      marketMoved=true;
    }
    if(applied===0&&!marketMoved){sRsError("Nothing applied — every row was unchecked.");return;}
    var libFile=rsFile,extraction=rsPreview.extraction;
    updateSettings({rateSheets:next});
    sRsPreview(null);sRsDataUrl(null);sRsFileName("");sRsFile(null);sRsSetMarket(false);
    flash();
    // Pricing is saved above, first and unconditionally; the library copy
    // runs after it and can never undo or block it.
    addSheetToLibrary(libFile,extraction);
  };

  // The same PDF goes into the Lender Library as a "Rate sheet" so Ask the
  // Library can answer from it (state lists, minimum values, margins…), and
  // that lender's older sheet is marked superseded. Fire-and-forget: the
  // outcome is the status line under the upload box.
  const addSheetToLibrary=function(file,extraction){
    if(!file||!extraction)return;
    var programs=[];
    (Array.isArray(extraction.products)?extraction.products:[]).forEach(function(p){var n=String((p&&p.product_name)||"").trim();if(n&&programs.indexOf(n)<0)programs.push(n);});
    var base="Adding this sheet to the Lender Library…";
    sRsLib({state:"working",text:base});
    var onProgress=function(p){
      if(!p||!p.message||p.stage==="error")return;
      sRsLib(function(s){return s&&s.state==="working"?{state:"working",text:base+" "+p.message}:s;});
    };
    Promise.resolve().then(function(){
      return addRateSheetToLibrary(file,{lender:extraction.lender,effectiveDate:extraction.effective_date,programs:programs},onProgress);
    }).then(function(res){
      var note=rateSheetLibraryNote(res);
      sRsLib({state:note.tone,text:note.text+"."});
    },function(err){
      var why=String((err&&err.message)||err||"unknown error").replace(/[\s.]+$/,"");
      sRsLib({state:"error",text:"Pricing is saved, but adding the sheet to the Lender Library failed: "+why+". You can add the PDF from the Lender Library screen."});
    });
  };

  const toggleRsRow=function(i){
    sRsPreview(function(p){if(!p)return p;var rows=p.rows.slice();rows[i]=Object.assign({},rows[i],{apply:!rows[i].apply});return Object.assign({},p,{rows:rows});});
  };
  const editRsRow=function(i,value){
    sRsPreview(function(p){if(!p)return p;var rows=p.rows.slice();var r=rows[i];var a=assessRateSheetChange(r.currentValue,value,r.currentBands,r.newBands);rows[i]=Object.assign({},r,{editedValue:value,warnings:a.warnings,bands:a.bands,changed:a.changed});return Object.assign({},p,{rows:rows});});
  };
  const [rcDiscover,sRcDiscover]=useState("");
  const [resetModal,sResetModal]=useState(false);
  const [resetTyped,sResetTyped]=useState("");
  const [wipeBusy,sWipeBusy]=useState(false);
  const [wipeErr,sWipeErr]=useState("");
  const rcRedirect=(typeof window!=="undefined"?window.location.origin:"")+"/rc-callback";

  const flash=()=>{sSaved(true);setTimeout(()=>sSaved(false),2500);};

  // Changes the SIGNED-IN person's own login. In production the login is
  // Supabase Auth (Reece and Max each have their own user), so this must go
  // through supabase.auth.updateUser — the old settings-hash write changed
  // nothing at login yet said "Settings saved". The hash is only the
  // local-dev fallback login (no Supabase configured).
  const saveMyPw=async ()=>{
    if(pwBusy)return;
    const pw=newAdminPw.trim();
    sPwOk("");
    if(!pw){sPwErr("Enter a new password.");return;}
    if(newAdminPw!==confirmAdminPw){sPwErr("Passwords do not match.");return;}
    if(pw.length<8){sPwErr("Password must be at least 8 characters.");return;}
    sPwBusy(true);sPwErr("");
    try{
      if(isSupabaseReady()){
        const {error}=await supabase.auth.updateUser({password:pw});
        if(error){sPwErr("Password NOT changed: "+(error.message||String(error)));return;}
        sPwOk("Password changed for "+me.name+". Use the new one next time you sign in.");
      }else{
        const hashed=await hashPassword(pw);
        updateSettings({adminPasswordHash:hashed});
        sPwOk("Local dev password changed (this browser's offline login, shared by both staff logins).");
      }
      sNewAdminPw("");sConfirmAdminPw("");
    }catch(e){sPwErr("Password NOT changed: "+((e&&e.message)||String(e)));}
    finally{sPwBusy(false);}
  };

  const savePhone=()=>{updateSettings({adminPhone:adminPhoneInp.trim()});flash();};
  // Reece's LO split (settings.rate_sheets._staffPrefs.reece.loSplit, merged
  // per staff key so nothing else in the sheet is touched) — plus the old
  // lo_split column, kept current. Max has no split at all (Reece, 10/1).
  const saveLoSplit=()=>{
    if(!isReece)return;
    const v=parseFloat(loSplitInp);if(isNaN(v)||v<0||v>100)return;
    const rs=settings.rateSheets||{};
    const prefs=Object.assign({},rs._staffPrefs);
    prefs[who]=Object.assign({},prefs[who],{loSplit:Math.round(v)});
    const patch={rateSheets:Object.assign({},rs,{_staffPrefs:prefs})};
    if(isReece)patch.loSplit=Math.round(v);
    updateSettings(patch);flash();
  };
  // "Lead assignment & Max on files" (src/features.js): Reece only, kept in
  // settings.rate_sheets._features.ownershipUi (an underscore key — the rate
  // sheet editors and the calculator skip it; the save merges it field by
  // field). Flipping it writes that one key and nothing else: no file or lead
  // changes, owners stay where they are.
  const ownershipOn=ownershipUiOn(settings);
  const toggleOwnershipUi=()=>{
    if(!isReece)return;
    const rs=settings.rateSheets||{};
    const next=withOwnershipUi(rs,!ownershipOn);
    if(next!==rs)updateSettings({rateSheets:next});
  };
  const saveHecmLimit=()=>{
    const v=parseInt(String(hecmLimitInp).replace(/[^0-9]/g,""),10);
    sHecmErr("");
    if(!v){sHecmErr("Enter the new limit.");return;}
    if(v<HECM_LIMIT_MIN||v>HECM_LIMIT_MAX){sHecmErr("$"+withCommas(v)+" isn't a HUD limit — it must be between $"+withCommas(HECM_LIMIT_MIN)+" and $"+withCommas(HECM_LIMIT_MAX)+". Check for an extra or missing digit.");return;}
    const cur=Number(settings.hecmLimit)||0;
    if(cur&&Math.abs(v-cur)/cur>0.15&&!window.confirm("Change the HECM limit from $"+withCommas(cur)+" to $"+withCommas(v)+"? That's a "+Math.round(Math.abs(v-cur)/cur*100)+"% change — HUD's yearly moves are usually a few percent."))return;
    updateSettings({hecmLimit:v});flash();
  };
  const addDmCamp=()=>{if(!newDmCamp.trim())return;updateSettings({dmCampaigns:[...settings.dmCampaigns,newDmCamp.trim()]});sNewDmCamp("");};
  const removeDmCamp=(i)=>{if(!window.confirm("Remove this campaign tag?"))return;updateSettings({dmCampaigns:settings.dmCampaigns.filter((_,idx)=>idx!==i)});};

  // Local dev only (ALLOW_DATA_WIPE is false in every production build) AND
  // only when no database is connected. Local dev points at the SAME Supabase
  // project as production, so a "local" wipe used to delete every live loan,
  // lead and expense. Now it can only ever clear this browser's local copy —
  // the button doesn't exist while a database is connected.
  const canWipe=ALLOW_DATA_WIPE&&isReece&&!isSupabaseReady();
  const resetAllData=()=>{if(!canWipe)return;sResetModal(true);sResetTyped("");sWipeErr("");};
  const runWipe=async()=>{
    if(!canWipe||wipeBusy||isSupabaseReady())return;
    sWipeBusy(true);sWipeErr("");
    try{localStorage.removeItem(LS_KEY);}catch(e){}
    window.location.reload();
  };

  const $=n=>"$"+Math.round(parseInt(String(n).replace(/[^0-9]/g,""))||0).toLocaleString();
  // The default-password banner only means something for the local-dev
  // fallback login; in production the hash column says nothing about the
  // real (Supabase Auth) password.
  const localDefaultPw=!isSupabaseReady()&&(settings.adminPasswordHash==="loanflow2026"||settings.adminPasswordHash==="UNSET-CHANGE-IN-SUPABASE-AUTH"||isLegacyPassword(String(settings.adminPasswordHash||"")));
  const rsCount=rsPreview?rsPreview.rows.filter(function(r){return r.apply;}).length:0;

  return(<div><Hdr t="Settings" s={isReece?"System configuration, access controls, and data management":"Signed in as "+me.name+" — your password and the shared rate sheets"}/>
    {saved&&<div style={{padding:"10px 16px",background:C.greenBg,border:"1px solid "+C.green+"40",borderRadius:8,marginBottom:14,color:C.green,fontSize:13,fontWeight:600}}>{Ic.chk} Settings saved</div>}

    <div style={{display:"flex",gap:20,flexWrap:"wrap"}}>

      {/* ── LEFT COLUMN ── */}
      <div style={{flex:"1 1 380px",display:"flex",flexDirection:"column",gap:16}}>

        {/* My Password — the signed-in person's own login */}
        <div style={{background:C.card,borderRadius:14,border:"1px solid "+C.red+"30",padding:20}}>
          <h3 style={{fontSize:14,fontWeight:700,color:C.red,margin:"0 0 4px"}}>🔐 Change my password</h3>
          <p style={{fontSize:11,color:C.textDim,margin:"0 0 14px"}}>Changes the login for <strong style={{color:C.text}}>{me.name}</strong> (the account signed in now). Minimum 8 characters.{isSupabaseReady()?"":" Local dev mode: this is the offline login stored in this browser."}</p>
          {localDefaultPw&&<div style={{padding:"10px 14px",background:C.redBg,borderRadius:8,border:"1px solid "+C.red+"40",marginBottom:14,display:"flex",alignItems:"flex-start",gap:8}}>
            <span style={{fontSize:16,flexShrink:0}}>⚠️</span>
            <div>
              <div style={{fontSize:12,fontWeight:700,color:C.red,marginBottom:2}}>Default local password active</div>
              <div style={{fontSize:11,color:C.textMuted}}>This browser's offline login is still on a default or unhashed password. Set a new one here.</div>
            </div>
          </div>}
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            <div><label style={{fontSize:10,fontWeight:700,color:C.textDim,display:"block",marginBottom:4}}>NEW PASSWORD</label><Inp type="password" placeholder="New password (min 8 chars)" value={newAdminPw} onChange={e=>{sNewAdminPw(e.target.value);sPwOk("");}}/></div>
            <div><label style={{fontSize:10,fontWeight:700,color:C.textDim,display:"block",marginBottom:4}}>CONFIRM PASSWORD</label><Inp type="password" placeholder="Re-enter new password" value={confirmAdminPw} onChange={e=>{sConfirmAdminPw(e.target.value);sPwOk("");}} onKeyDown={e=>e.key==="Enter"&&saveMyPw()}/></div>
            {pwErr&&<div style={{fontSize:12,color:C.red}}>{pwErr}</div>}
            {pwOk&&<div style={{fontSize:12,color:C.green,fontWeight:600}}>{Ic.chk} {pwOk}</div>}
            <Btn primary small onClick={saveMyPw} disabled={pwBusy}>{pwBusy?"Changing…":"Change my password"}</Btn>
          </div>
        </div>

        {/* Admin Phone — Reece's notification number */}
        {isReece&&<div style={{background:C.card,borderRadius:14,border:"1px solid "+C.border,padding:20}}>
          <h3 style={{fontSize:14,fontWeight:700,color:C.accentLight,margin:"0 0 4px"}}>📱 Your Phone Number</h3>
          <p style={{fontSize:11,color:C.textDim,margin:"0 0 12px"}}>Used for SMS notifications sent to you (new leads, callbacks, document uploads).</p>
          <div style={{display:"flex",gap:8}}>
            <Inp placeholder="(555) 555-5555" value={adminPhoneInp} onChange={e=>sAdminPhoneInp(e.target.value)} style={{flex:1}}/>
            <Btn primary small onClick={savePhone}>Save</Btn>
          </div>
          {settings.adminPhone&&<div style={{fontSize:11,color:C.textDim,marginTop:6}}>Current: <span style={{color:C.accentLight,fontFamily:"'Mulish',system-ui,sans-serif"}}>{settings.adminPhone}</span></div>}
        </div>}

        {/* LO Split — Reece's own (Max has none) */}
        {isReece&&<div style={{background:C.card,borderRadius:14,border:"1px solid "+C.border,padding:20}}>
          <h3 style={{fontSize:14,fontWeight:700,color:C.green,margin:"0 0 4px"}}>💰 My LO Commission Split</h3>
          <p style={{fontSize:11,color:C.textDim,margin:"0 0 12px"}}>{me.name}'s personal cut of broker compensation. Used only on the Expenses & Income page — all other pages show full broker comp.</p>
          <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
            <Inp placeholder="70" value={loSplitInp} onChange={e=>sLoSplitInp(e.target.value)} onKeyDown={e=>e.key==="Enter"&&saveLoSplit()} style={{width:80,fontFamily:"'Mulish',system-ui,sans-serif"}}/>
            <span style={{fontSize:13,color:C.textMuted}}>%</span>
            <Btn primary small onClick={saveLoSplit}>Save</Btn>
            <span style={{fontSize:11,color:C.textDim}}>Current: <strong style={{color:C.green}}>{mySplit}% LO / {100-mySplit}% Broker</strong></span>
          </div>
        </div>}

        {/* HECM Limit — shared pricing input, Reece edits it */}
        <div style={{background:C.card,borderRadius:14,border:"1px solid "+C.border,padding:20}}>
          <h3 style={{fontSize:14,fontWeight:700,color:C.accent,margin:"0 0 4px"}}>🏠 HECM Lending Limit</h3>
          <p style={{fontSize:11,color:C.textDim,margin:"0 0 12px"}}>HUD adjusts this annually. Update here when the new limit takes effect. Used in calculator and MCA calculations.</p>
          {isReece?<>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <div style={{position:"relative",flex:1}}><span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",color:C.textDim,fontSize:13,zIndex:1}}>$</span><MoneyInp value={hecmLimitInp} onChange={e=>{sHecmLimitInp(e.target.value);sHecmErr("");}} style={{paddingLeft:22}}/></div>
              <Btn primary small onClick={saveHecmLimit}>Save</Btn>
            </div>
            {hecmErr&&<div style={{fontSize:12,color:C.red,marginTop:6}}>{hecmErr}</div>}
          </>:null}
          <div style={{fontSize:11,color:C.textDim,marginTop:6}}>Current: <span style={{color:C.accentLight,fontFamily:"'Mulish',system-ui,sans-serif"}}>{$( settings.hecmLimit)}</span>{!isReece&&" · Reece updates this"}</div>
        </div>

        {/* Rate Sheet PDF Upload */}
        <div style={{background:C.card,borderRadius:14,border:"1px solid "+C.border,padding:20}}>
          <h3 style={{fontSize:14,fontWeight:700,color:C.purple,margin:"0 0 4px"}}>📄 Upload Rate Sheet PDF</h3>
          <p style={{fontSize:11,color:C.textDim,margin:"0 0 14px"}}>Drop a lender rate sheet PDF and Claude will extract the rates. You'll review every value before it's applied to the calculator — nothing auto-commits. The rate sheets are shared, so a change here prices for both of you. Applying also adds the PDF to the Lender Library, so Ask the Library can answer from it (state lists, minimum values, pricing) and the older sheet from that lender is marked superseded.</p>

          {!rsPreview&&<>
            <label style={{display:"flex",flexDirection:"column",alignItems:"center",padding:22,border:"2px dashed "+C.borderLight,borderRadius:10,cursor:"pointer",background:C.surfaceAlt,marginBottom:10}}>
              <span style={{color:C.purple,marginBottom:6}}>{Ic.upload}</span>
              <span style={{fontSize:13,fontWeight:600,color:C.text,textAlign:"center"}}>{rsFileName||"Click to choose rate sheet PDF"}</span>
              <span style={{fontSize:11,color:C.textDim,marginTop:3}}>FOA, MoO, or any lender — max {MAX_RATE_SHEET_LABEL}</span>
              <input type="file" accept=".pdf,application/pdf" onChange={onPickRateSheet} style={{display:"none"}}/>
            </label>
            {rsError&&<div style={{padding:"8px 12px",background:C.redBg,border:"1px solid "+C.red+"30",borderRadius:6,fontSize:12,color:C.red,marginBottom:10}}>{rsError}</div>}
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <Btn primary small disabled={!rsDataUrl||rsLoading} onClick={runRateSheetExtraction}>{rsLoading?"Extracting…":"Extract Rates"}</Btn>
              {rsFileName&&!rsLoading&&<Btn small onClick={function(){sRsFileName("");sRsDataUrl(null);sRsFile(null);sRsError("");}} style={{color:C.textDim}}>Clear</Btn>}
              {rsLoading&&<span style={{fontSize:11,color:C.textDim,fontStyle:"italic"}}>Reading PDF & matching products…</span>}
            </div>
          </>}

          {rsPreview&&<>
            <div style={{padding:"10px 12px",background:C.surfaceAlt,borderRadius:8,marginBottom:12}}>
              <div style={{display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:8,fontSize:11}}>
                <span style={{color:C.textMuted}}>Lender: <strong style={{color:C.accentLight}}>{rsPreview.extraction.lender||"Unknown"}</strong></span>
                <span style={{color:C.textMuted}}>Effective: <strong style={{color:C.accentLight,fontFamily:"'Mulish',system-ui,sans-serif"}}>{rsPreview.extraction.effective_date||"—"}</strong></span>
                <span style={{color:C.textMuted}}>1yr CMT: <strong style={{color:C.accentLight,fontFamily:"'Mulish',system-ui,sans-serif"}}>{rsPreview.extraction.index_1yr_cmt||"—"}%</strong></span>
                <span style={{color:C.textMuted}}>10yr: <strong style={{color:C.accentLight,fontFamily:"'Mulish',system-ui,sans-serif"}}>{rsPreview.extraction.index_10yr||"—"}%</strong></span>
              </div>
              {(pdfSwap!=null||pdfCmt!=null)&&<div style={{marginTop:8,paddingTop:8,borderTop:"1px dashed "+C.border,fontSize:11,color:C.textMuted,lineHeight:1.5}}>
                <div>Today's Market now: <strong style={{color:C.text}}>{rsCur.swapRate||"—"}% swap · {rsCur.index||"—"}% CMT</strong>{lastMark?" (last set "+(lastMark.date||new Date(lastMark.at).toLocaleDateString())+")":""}. Applying rows never changes it.</div>
                {marketDiffers?<label style={{display:"flex",alignItems:"flex-start",gap:6,marginTop:4,cursor:"pointer"}}>
                  <input type="checkbox" checked={rsSetMarket} onChange={e=>sRsSetMarket(e.target.checked)} style={{marginTop:2,accentColor:C.accent}}/>
                  <span>Also set Today's Market to <strong style={{color:C.text}}>{pdfSwap!=null?pdfSwap+"% swap":""}{pdfSwap!=null&&pdfCmt!=null?" / ":""}{pdfCmt!=null?pdfCmt+"% CMT":""}</strong> — moves every lender's sheet together and stamps swap history.{sheetNewerThanMarket?<span style={{color:C.accent,fontWeight:600}}> This sheet is newer than your last market update.</span>:<span style={{color:C.orange,fontWeight:600}}> This sheet is not newer than your last market update — usually leave this off.</span>}</span>
                </label>:<div style={{marginTop:2}}>The sheet's numbers match Today's Market.</div>}
              </div>}
            </div>
            <div style={{fontSize:11,color:C.textDim,marginBottom:8}}>Review each product. Uncheck rows you don't want to apply. Edit the "New" field if Claude misread a value. Rows with a ⚠ start unchecked — they'd lose information the current sheet has.</div>
            <div style={{maxHeight:360,overflowY:"auto",border:"1px solid "+C.border,borderRadius:8,marginBottom:10}}>
              {rsPreview.rows.length===0?<div style={{padding:20,textAlign:"center",color:C.textDim,fontSize:12}}>No products matched your current rate-sheet keys.</div>:
                rsPreview.rows.map(function(r,i){
                  var changed=r.changed;
                  return(<div key={r.appKey+"-"+i} style={{padding:"10px 12px",borderBottom:i<rsPreview.rows.length-1?"1px solid "+C.border:"none",background:r.apply?C.accentGlow:"transparent"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                      <input type="checkbox" checked={r.apply} onChange={function(){toggleRsRow(i);}} style={{cursor:"pointer",accentColor:C.accent}}/>
                      <span style={{fontSize:12,fontWeight:700,color:C.text,flex:1}}>{r.label}</span>
                      <span style={{fontSize:10,color:C.textDim,fontFamily:"'Mulish',system-ui,sans-serif"}}>{r.appKey}</span>
                      {r.warnings&&r.warnings.length>0?<Badge label="⚠ Check" color={C.red} bg={C.redBg}/>:changed?<Badge label="Changed" color={C.orange} bg={C.orangeBg}/>:<Badge label="Same" color={C.textDim} bg={C.surfaceAlt}/>}
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"80px 1fr",gap:6,fontSize:10,alignItems:"center"}}>
                      <span style={{color:C.textDim}}>Current:</span>
                      <span style={{fontFamily:"'Mulish',system-ui,sans-serif",color:C.textMuted,fontSize:11,wordBreak:"break-all"}}>{r.currentValue||<em style={{color:C.textDim}}>empty</em>}</span>
                      <span style={{color:C.textDim}}>New:</span>
                      <input value={r.editedValue} onChange={function(e){editRsRow(i,e.target.value);}} style={{fontFamily:"'Mulish',system-ui,sans-serif",fontSize:11,padding:"4px 8px",borderRadius:4,border:"1px solid "+(changed?C.orange+"60":C.border),background:C.surfaceAlt,color:C.text,outline:"none",width:"100%",boxSizing:"border-box"}}/>
                      {(r.bands||r.currentBands)&&<><span style={{color:C.textDim}}>PLU bands:</span><span style={{fontSize:11,color:r.bands?C.orange:C.textMuted}}>{r.bands?(r.currentBands||"none")+" → "+r.bands:r.currentBands}</span></>}
                    </div>
                    {r.warnings&&r.warnings.map(function(w,wi){return<div key={wi} style={{fontSize:11,color:C.red,fontWeight:600,marginTop:4}}>⚠ {w}</div>;})}
                  </div>);
                })
              }
            </div>
            {rsPreview.unmatched.length>0&&<div style={{padding:"10px 12px",background:C.orangeBg,border:"1px solid "+C.orange+"30",borderRadius:8,marginBottom:10}}>
              <div style={{fontSize:11,fontWeight:700,color:C.orange,marginBottom:4}}>{rsPreview.unmatched.length} product{rsPreview.unmatched.length!==1?"s":""} not yet wired into the calculator</div>
              <div style={{fontSize:10,color:C.textMuted}}>{rsPreview.unmatched.map(function(u){return u.product_name;}).join(", ")}</div>
              <div style={{fontSize:10,color:C.textDim,marginTop:4}}>These show up in the extraction but need calc-logic support before they can be quoted. Ask Claude to wire them in when you're ready to add that lender.</div>
            </div>}
            {rsError&&<div style={{padding:"8px 12px",background:C.redBg,border:"1px solid "+C.red+"30",borderRadius:6,fontSize:12,color:C.red,marginBottom:10}}>{rsError}</div>}
            <div style={{display:"flex",gap:8}}>
              <Btn primary small onClick={applyRateSheetPreview} disabled={rsCount===0&&!rsSetMarket}>Apply Checked ({rsCount}){rsSetMarket?" + market":""}</Btn>
              <Btn small onClick={function(){sRsPreview(null);sRsDataUrl(null);sRsFileName("");sRsFile(null);sRsSetMarket(false);sRsError("");}} style={{color:C.textDim}}>Cancel</Btn>
            </div>
          </>}
          {rsLib&&<div role="status" style={{marginTop:10,fontSize:11,lineHeight:1.5,fontWeight:600,color:rsLib.state==="ok"?C.green:rsLib.state==="warn"?C.orange:rsLib.state==="error"?C.red:C.textDim}}>
            {rsLib.state==="ok"?<>{Ic.chk} </>:null}{rsLib.text}
          </div>}
        </div>

        {/* DM Campaign Tags */}
        <div style={{background:C.card,borderRadius:14,border:"1px solid "+C.border,padding:20}}>
          <h3 style={{fontSize:14,fontWeight:700,color:C.cyan,margin:"0 0 4px"}}>📬 Direct Mail Campaign Tags</h3>
          <p style={{fontSize:11,color:C.textDim,margin:"0 0 12px"}}>These tags appear in the New Lead form and Lead Database when source is "Mailer".</p>
          <div style={{display:"flex",flexDirection:"column",gap:4,marginBottom:12}}>
            {settings.dmCampaigns.map((c,i)=><div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 10px",background:C.surfaceAlt,borderRadius:6}}>
              <span style={{fontSize:12,color:C.text,fontFamily:"'Mulish',system-ui,sans-serif"}}>{c}</span>
              <button onClick={()=>removeDmCamp(i)} style={{background:"none",border:"none",color:C.red,cursor:"pointer",fontSize:11}}>✕</button>
            </div>)}
          </div>
          <div style={{display:"flex",gap:8}}>
            <Inp placeholder="e.g. DM-2026-Q1-Equity" value={newDmCamp} onChange={e=>sNewDmCamp(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addDmCamp()} style={{flex:1}}/>
            <Btn primary small onClick={addDmCamp}>+ Add</Btn>
          </div>
        </div>
      </div>

      {/* ── RIGHT COLUMN — Reece-only admin items ── */}
      {isReece&&<div style={{flex:"1 1 320px",display:"flex",flexDirection:"column",gap:16}}>

        {/* Lead assignment & Max on files (src/features.js, 10/10) — off by
            default; one switch brings the owner chips / Whose files /
            reassigning back exactly as they were. */}
        <div data-testid="ownership-ui-card" style={{background:C.card,borderRadius:14,border:"1px solid "+C.border,padding:20}}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <h3 id="ownership-ui-label" style={{fontSize:14,fontWeight:700,color:C.accentLight,margin:0,flex:1}}>👥 Lead assignment &amp; Max on files</h3>
            <button type="button" role="switch" aria-checked={ownershipOn} aria-labelledby="ownership-ui-label" aria-describedby="ownership-ui-help" data-ownership-ui-switch="" onClick={toggleOwnershipUi}
              style={{width:44,height:24,borderRadius:12,border:"none",padding:2,background:ownershipOn?C.accent:C.cream2,display:"inline-flex",alignItems:"center",justifyContent:ownershipOn?"flex-end":"flex-start",cursor:"pointer",flexShrink:0}}>
              <span style={{width:20,height:20,borderRadius:10,background:"#fff",boxShadow:"0 1px 2px rgba(6,26,48,0.3)"}}/>
            </button>
          </div>
          <p id="ownership-ui-help" style={{fontSize:11,color:C.textMuted,margin:"8px 0 0",lineHeight:1.5}}>Off: no owner chips, no assigning, every file shows. Turn on to bring it back.</p>
        </div>

        {/* RingCentral Integration */}
        <div style={{background:C.card,borderRadius:14,border:"1px solid "+C.border,padding:20}}>
          <h3 style={{fontSize:14,fontWeight:700,color:C.accent,margin:"0 0 4px"}}>☎️ RingCentral Integration</h3>
          <p style={{fontSize:11,color:C.textDim,margin:"0 0 14px"}}>Click-to-call launches the RingCentral desktop app. Inbound calls trigger a toast that matches the caller to a loan or marketing lead.</p>

          {/* Enable toggle */}
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 14px",background:C.surfaceAlt,borderRadius:8,marginBottom:12}}>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:C.text}}>Integration {settings.rcEnabled?"Enabled":"Disabled"}</div>
              <div style={{fontSize:11,color:C.textDim,marginTop:2}}>{settings.rcEnabled?(rcAuthed?(rcStatus==="connected"?"Connected — listening for inbound calls":rcStatus==="connecting"?"Connecting…":rcStatus==="error"?"Subscribe failed — check client ID":"Authenticated, waiting for session"):"Connect your RingCentral account below"):"Click-to-call + inbound detection off"}</div>
            </div>
            <button onClick={()=>{updateSettings({rcEnabled:!settings.rcEnabled});flash();}} style={{width:44,height:24,borderRadius:12,border:"none",background:settings.rcEnabled?C.green:C.borderLight,cursor:"pointer",position:"relative",flexShrink:0}}>
              <div style={{width:18,height:18,borderRadius:"50%",background:C.white,position:"absolute",top:3,left:settings.rcEnabled?23:3,transition:"left 0.2s"}}/>
            </button>
          </div>

          <div style={{marginBottom:10}}>
            <label style={{fontSize:10,fontWeight:700,color:C.textDim,display:"block",marginBottom:4}}>CLIENT ID</label>
            <div style={{display:"flex",gap:8}}>
              <Inp placeholder="RingCentral app Client ID" value={rcClientIdInp} onChange={e=>sRcClientIdInp(e.target.value)} style={{flex:1,fontFamily:"'Mulish',system-ui,sans-serif",fontSize:12}}/>
              <Btn primary small onClick={()=>{updateSettings({rcClientId:rcClientIdInp.trim()});flash();}}>Save</Btn>
            </div>
          </div>

          <div style={{marginBottom:10}}>
            <label style={{fontSize:10,fontWeight:700,color:C.textDim,display:"block",marginBottom:4}}>PLATFORM SERVER (OPTIONAL)</label>
            <div style={{display:"flex",gap:8}}>
              <Inp placeholder="https://platform.ringcentral.com" value={rcServerInp} onChange={e=>sRcServerInp(e.target.value)} style={{flex:1,fontFamily:"'Mulish',system-ui,sans-serif",fontSize:11}}/>
              <Btn primary small onClick={()=>{updateSettings({rcServerUrl:rcServerInp.trim()});flash();}}>Save</Btn>
            </div>
            <div style={{fontSize:10,color:C.textDim,marginTop:4}}>Leave blank for production. Use <span style={{fontFamily:"'Mulish',system-ui,sans-serif",color:C.accentLight}}>https://platform.devtest.ringcentral.com</span> for sandbox.</div>
          </div>

          <div style={{padding:"10px 12px",background:C.surfaceAlt,borderRadius:8,marginBottom:10}}>
            <div style={{fontSize:10,color:C.textDim,fontWeight:700,marginBottom:4}}>REDIRECT URI (register this in your RC app)</div>
            <div style={{fontSize:11,color:C.accentLight,fontFamily:"'Mulish',system-ui,sans-serif",wordBreak:"break-all"}}>{rcRedirect}</div>
          </div>

          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            {rcAuthed?<>
              <Badge label="Authenticated" color={C.green} bg={C.greenBg}/>
              <Btn small danger onClick={()=>{RC.clearTokens();RC.setSmsFromOverride("");setRcAuthed(false);flash();}}>Disconnect</Btn>
            </>:<>
              <Btn small primary disabled={!settings.rcClientId} onClick={async()=>{try{await RC.startOAuth(settings);}catch(e){alert("RC auth failed: "+e.message);}}}>Connect RingCentral</Btn>
              {!settings.rcClientId&&<span style={{fontSize:11,color:C.textDim}}>Save a Client ID first</span>}
            </>}
          </div>
          <div data-testid="rc-calllog-note" style={{fontSize:11,color:C.textMuted,marginTop:8,lineHeight:1.5}}>Calls to return (Command Center, New Call) reads your RingCentral call log — it needs the <strong>Read Call Log</strong> permission on your RingCentral app; after adding it, Disconnect / Connect here.</div>

          <div style={{marginTop:12}}>
            <label style={{fontSize:10,fontWeight:700,color:C.textDim,display:"block",marginBottom:4}}>CLICK-TO-CALL OPENS (THIS DEVICE)</label>
            <select value={callMode} onChange={function(e){const v=e.target.value;try{RC.setCallLaunchMode(v);}catch(err){}sCallMode(v);flash();}} style={{width:"100%",padding:"9px 12px",borderRadius:8,border:"1px solid "+C.border,background:C.surfaceAlt,color:C.text,fontSize:12,fontFamily:"'Mulish',system-ui,sans-serif"}}>
              <option value="auto">RingCentral app, phone app if it doesn't open</option>
              <option value="rcapp">RingCentral app only</option>
              <option value="tel">Phone app (tel:) only</option>
            </select>
          </div>

          {/* ── Inquiry SMS ── */}
          <div style={{marginTop:16,paddingTop:14,borderTop:"1px dashed "+C.border}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
              <span style={{fontSize:12,fontWeight:700,color:C.green}}>📱 Live Inquiry Notifications</span>
              {rcAuthed&&settings.rcEnabled&&(settings.rcSmsFrom||rcDiscover.startsWith("+"))?<Badge label="Live" color={C.green} bg={C.greenBg}/>:<Badge label="Not sending" color={C.orange} bg={C.orangeBg}/>}
            </div>
            <p style={{fontSize:11,color:C.textDim,margin:"0 0 10px",lineHeight:1.5}}>Every borrower-portal inquiry, callback request, and document upload texts <span style={{color:C.accentLight,fontFamily:"'Mulish',system-ui,sans-serif"}}>+1 302-893-8949</span> via RingCentral. Requires your RC app to have the <strong>SMS and MMS</strong> permission and an SMS-capable phone number on the extension.</p>

            <label style={{fontSize:10,fontWeight:700,color:C.textDim,display:"block",marginBottom:4}}>SMS FROM NUMBER</label>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <Inp placeholder="+14805551234" value={rcSmsFromInp} onChange={e=>sRcSmsFromInp(e.target.value)} style={{flex:1,fontFamily:"'Mulish',system-ui,sans-serif",fontSize:12}}/>
              <Btn primary small onClick={()=>{updateSettings({rcSmsFrom:rcSmsFromInp.trim()});RC.setSmsFromOverride(rcSmsFromInp.trim());flash();}}>Save</Btn>
              <Btn small disabled={!rcAuthed} onClick={async()=>{
                try{
                  RC.setSmsFromOverride("");
                  var n=await RC.discoverSmsFromNumber(settings);
                  if(n){sRcSmsFromInp(n);sRcDiscover(n);updateSettings({rcSmsFrom:n});flash();}
                  else sRcDiscover("No SMS-capable number found on this extension.");
                }catch(e){sRcDiscover("Discovery failed: "+e.message);}
              }} style={{color:C.cyan}}>Auto-detect</Btn>
            </div>
            {rcDiscover&&!rcDiscover.startsWith("+")&&<div style={{fontSize:11,color:C.orange,marginTop:6}}>{rcDiscover}</div>}
            {!rcAuthed&&<div style={{fontSize:10,color:C.textDim,marginTop:6}}>Connect RingCentral above to enable live sending.</div>}
          </div>
        </div>

        {/* Data Stats */}
        <div style={{background:C.card,borderRadius:14,border:"1px solid "+C.border,padding:20}}>
          <h3 style={{fontSize:14,fontWeight:700,color:C.text,margin:"0 0 14px"}}>📊 Data Overview</h3>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {[
              {l:"Pipeline files",v:loans.length},
              {l:"Borrower portal accounts",v:borrowerAccts.length},
              {l:allLeadsLoaded===false?"Marketing leads loaded (mailed/worked)":"Marketing DB leads",v:mktgLeads.length},
              {l:"Campaigns",v:campaigns.length},
              {l:"Expenses logged (yours)",v:expenses.length},
              {l:"SMS notifications",v:smsLog.length},
            ].map(row=><div key={row.l} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid "+C.border}}>
              <span style={{fontSize:12,color:C.textMuted}}>{row.l}</span>
              <span style={{fontSize:12,fontWeight:700,color:C.text,fontFamily:"'Mulish',system-ui,sans-serif"}}>{row.v}</span>
            </div>)}
          </div>
          <div style={{marginTop:12,padding:"8px 12px",background:C.greenBg,borderRadius:6,fontSize:11,color:C.green}}>
            {Ic.chk} Data saved automatically after every change.
          </div>

          {/* Download Full Backup — exports everything to a timestamped JSON
              the user can save anywhere (OneDrive, local disk, email, etc.).
              Defense-in-depth against silent save failures. */}
          <div style={{marginTop:14,padding:"12px 14px",background:C.accentGlow,border:"1px solid "+C.accent+"40",borderRadius:8}}>
            <div style={{fontSize:12,fontWeight:700,color:C.accentLight,marginBottom:4}}>💾 Download Full Backup</div>
            <div style={{fontSize:11,color:C.textMuted,marginBottom:10,lineHeight:1.5}}>Exports every table (loans, leads, campaigns, your expenses, mailings history, SMS log, borrower accounts, settings) into a single JSON file. Save this anywhere you want a recoverable copy — local disk, OneDrive, email to yourself.</div>
            <Btn primary small onClick={async function(){
              // A backup must include the never-mailed leads too, which are not
              // loaded at login. Fetch them first; the next click downloads.
              if(allLeadsLoaded===false&&loadAllLeads){
                var ok=await loadAllLeads();
                window.alert(ok?"All leads are loaded now — click Download Backup Now again to save the complete file.":"Couldn't load the full lead list. Check your connection and try again.");
                return;
              }
              var payload={
                _meta:{exportedAt:new Date().toISOString(),version:"1.0",source:"LoanFlow CRM Full Backup"},
                loans:loans,
                mktgLeads:mktgLeads,
                campaigns:campaigns,
                expenses:expenses,
                borrowerAccts:borrowerAccts,
                smsLog:smsLog,
                settings:Object.assign({},settings,{adminPasswordHash:"[REDACTED]"}), // don't backup password hash
              };
              var json=JSON.stringify(payload,null,2);
              var blob=new Blob([json],{type:"application/json"});
              var url=URL.createObjectURL(blob);
              var a=document.createElement("a");
              var ts=new Date().toISOString().replace(/[:.]/g,"-").slice(0,19);
              a.href=url;a.download="loanflow-backup-"+ts+".json";
              document.body.appendChild(a);a.click();
              setTimeout(function(){document.body.removeChild(a);URL.revokeObjectURL(url);},100);
            }}>{Ic.upload} Download Backup Now</Btn>
            <div style={{fontSize:10,color:C.textDim,marginTop:8}}>Tip: do this before and after any bulk operation (lead import, mail log batch) so you have a snapshot to restore from if anything goes wrong.</div>
          </div>

          {/* Restore from Backup — re-imports a JSON download (leads-only or
              full backup). Merges mailings/history onto matching leads by id,
              inserts unmatched ones as new. Never overwrites name/address/etc
              on existing leads. */}
          <div style={{marginTop:14,padding:"12px 14px",background:C.surfaceAlt,border:"1px solid "+C.border,borderRadius:8}}>
            <div style={{fontSize:12,fontWeight:700,color:C.text,marginBottom:4}}>♻️ Restore from Backup</div>
            <div style={{fontSize:11,color:C.textMuted,marginBottom:10,lineHeight:1.5}}>Upload a backup JSON (the leads-only file auto-downloaded after each bulk mail-log, or a full backup from the button above). Merges mailings &amp; history onto existing leads by ID — won&apos;t overwrite names, addresses, or statuses.</div>
            <label style={{display:"inline-block"}}>
              <input type="file" accept=".json,application/json" style={{display:"none"}} onChange={onPickRestoreBackup}/>
              <span style={{display:"inline-flex",alignItems:"center",gap:6,padding:"8px 14px",borderRadius:8,background:C.surfaceAlt,border:"1px solid "+C.cyan,color:C.cyan,fontSize:12,fontWeight:600,cursor:"pointer"}}>{Ic.upload} Choose Backup File…</span>
            </label>
            {restoreError&&!restoreFile&&<div style={{marginTop:8,padding:"6px 10px",background:C.red+"20",border:"1px solid "+C.red+"40",borderRadius:6,fontSize:11,color:C.red}}>{restoreError}</div>}
            {restoreDone&&<div style={{marginTop:8,padding:"6px 10px",background:C.green+"20",border:"1px solid "+C.green+"40",borderRadius:6,fontSize:11,color:C.green}}>{Ic.chk} {restoreDone}</div>}
          </div>
        </div>

        {/* Restore Confirmation Modal */}
        {restoreFile&&<div style={{position:"fixed",inset:0,background:"rgba(6,26,48,0.55)",zIndex:9000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={closeRestore}>
          <div onClick={e=>e.stopPropagation()} style={{background:C.card,borderRadius:14,border:"1px solid "+C.border,padding:24,maxWidth:520,width:"100%",boxShadow:"0 20px 60px rgba(11,42,74,0.16)"}}>
            <h3 style={{fontSize:16,fontWeight:700,color:C.text,margin:"0 0 4px"}}>Confirm Restore</h3>
            <p style={{fontSize:11,color:C.textDim,margin:"0 0 14px"}}>{restoreFile.fileName}{restoreFile.exportedAt?" · exported "+new Date(restoreFile.exportedAt).toLocaleString():""}</p>
            {!restorePreview?<div style={{padding:"18px 14px",background:C.surfaceAlt,borderRadius:8,border:"1px solid "+C.border,marginBottom:14,fontSize:12,color:C.textMuted,textAlign:"center"}}>
              {restoreLoading?"Loading the full prospect list first (never-mailed leads aren't loaded at login) so nothing live is mistaken for \"new\"…":"Waiting for the full prospect list…"}
              <div style={{fontSize:11,color:C.textDim,marginTop:6}}>{restoreFile.fileCount} leads in the file · {mktgLeads.length} loaded so far</div>
            </div>:<div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
              <div style={{background:C.surfaceAlt,borderRadius:8,padding:"10px 12px",border:"1px solid "+C.border}}>
                <div style={{fontSize:10,color:C.textDim,fontWeight:600,textTransform:"uppercase",letterSpacing:0.5}}>Leads in file</div>
                <div style={{fontSize:22,fontWeight:700,color:C.text,fontFamily:"'Mulish',system-ui,sans-serif"}}>{restorePreview.fileCount}</div>
              </div>
              <div style={{background:C.surfaceAlt,borderRadius:8,padding:"10px 12px",border:"1px solid "+C.border}}>
                <div style={{fontSize:10,color:C.textDim,fontWeight:600,textTransform:"uppercase",letterSpacing:0.5}}>Existing leads to update</div>
                <div style={{fontSize:22,fontWeight:700,color:C.accentLight,fontFamily:"'Mulish',system-ui,sans-serif"}}>{restorePreview.leadsTouched}</div>
              </div>
              <div style={{background:C.surfaceAlt,borderRadius:8,padding:"10px 12px",border:"1px solid "+C.border}}>
                <div style={{fontSize:10,color:C.textDim,fontWeight:600,textTransform:"uppercase",letterSpacing:0.5}}>Mailings to add</div>
                <div style={{fontSize:22,fontWeight:700,color:C.green,fontFamily:"'Mulish',system-ui,sans-serif"}}>{restorePreview.mailingsAdded}</div>
              </div>
              <div style={{background:C.surfaceAlt,borderRadius:8,padding:"10px 12px",border:"1px solid "+C.border}}>
                <div style={{fontSize:10,color:C.textDim,fontWeight:600,textTransform:"uppercase",letterSpacing:0.5}}>New leads to insert</div>
                <div style={{fontSize:22,fontWeight:700,color:C.cyan,fontFamily:"'Mulish',system-ui,sans-serif"}}>{restorePreview.newLeads}</div>
              </div>
            </div>}
            <div style={{padding:"8px 12px",background:C.accentGlow,borderRadius:6,fontSize:11,color:C.textMuted,marginBottom:14,lineHeight:1.5}}>Existing leads keep their current name, address, phone, email, and status. Only new mailings &amp; history entries are appended. Duplicates (same mailing ID) are skipped.</div>
            {restoreError&&<div style={{marginBottom:12,padding:"6px 10px",background:C.red+"20",border:"1px solid "+C.red+"40",borderRadius:6,fontSize:11,color:C.red}}>{restoreError}{!restoreLoading&&needAllLeads&&loadAllLeads&&<button onClick={()=>{sRestoreError("");restoreLoadRef.current=false;sRestoreFile(f=>f?Object.assign({},f):f);}} style={{marginLeft:8,background:"none",border:"none",color:C.accent,cursor:"pointer",fontSize:11,fontWeight:700,textDecoration:"underline"}}>Try again</button>}</div>}
            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <Btn small onClick={closeRestore}>Cancel</Btn>
              <Btn primary small onClick={applyRestore} disabled={!restorePreview||(restorePreview.mailingsAdded===0&&restorePreview.newLeads===0&&restorePreview.historyAdded===0)}>{Ic.chk} Apply Restore</Btn>
            </div>
          </div>
        </div>}

        {/* SMS Log */}
        <div style={{background:C.card,borderRadius:14,border:"1px solid "+C.border,padding:20}}>
          <h3 style={{fontSize:14,fontWeight:700,color:C.accentLight,margin:"0 0 4px"}}>📲 Inquiry Notifications</h3>
          <p style={{fontSize:11,color:C.textDim,margin:"0 0 12px"}}>Live texts to <span style={{color:C.accentLight,fontFamily:"'Mulish',system-ui,sans-serif"}}>+1 302-893-8949</span> for every borrower-side inquiry. Delivery runs through RingCentral — see the Live status above.</p>
          <div style={{padding:"8px 12px",background:C.surfaceAlt,borderRadius:6,marginBottom:10}}>
            {[{l:"New portal lead",dir:"→ You"},{l:"Borrower uploads docs",dir:"→ You"},{l:"Callback request",dir:"→ You"}].map(t=><div key={t.l} style={{display:"flex",justifyContent:"space-between",padding:"3px 0",fontSize:11}}>
              <span style={{color:C.textMuted}}>{t.l}</span>
              <span style={{color:t.dir.includes("You")?C.accent:C.green,fontWeight:600,fontSize:10}}>{t.dir}</span>
            </div>)}
          </div>
          {smsLog.length===0?<div style={{padding:12,textAlign:"center",color:C.textDim,fontSize:12}}>No notifications sent yet.</div>
          :<div style={{maxHeight:200,overflowY:"auto",display:"flex",flexDirection:"column",gap:5}}>
            {smsLog.slice(0,20).map(sms=>{
              var delivery=sms.delivery||"sent";
              var barColor=delivery==="sent"?C.green:delivery==="failed"?C.red:C.orange;
              var deliveryLabel=delivery==="sent"?"Delivered":delivery==="failed"?"Failed":"Pending";
              return(<div key={sms.id} style={{padding:"7px 10px",background:C.surfaceAlt,borderRadius:5,borderLeft:"3px solid "+barColor}}>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:C.textDim,marginBottom:2}}>
                  <span style={{color:barColor,fontWeight:600}}>{deliveryLabel}</span>
                  <span style={{fontFamily:"'Mulish',system-ui,sans-serif"}}>{sms.date} {sms.time}</span>
                </div>
                <div style={{fontSize:11,color:C.textMuted}}>{sms.message.length>100?sms.message.substring(0,100)+"...":sms.message}</div>
                {sms.error&&<div style={{fontSize:10,color:C.red,marginTop:3}}>Error: {sms.error}</div>}
              </div>);
            })}
          </div>}
        </div>

        {/* Danger Zone — local dev with NO database connected only (never in
            a production build, never while Supabase is connected), never for Max */}
        {canWipe&&<div style={{background:C.card,borderRadius:14,border:"1px solid "+C.red+"30",padding:20}}>
          <h3 style={{fontSize:14,fontWeight:700,color:C.red,margin:"0 0 6px"}}>⚠️ Danger Zone (this browser's local data)</h3>
          <p style={{fontSize:11,color:C.textDim,margin:"0 0 12px"}}>No database is connected, so everything here lives only in this browser. This clears that local copy and resets the app to its initial state. It cannot be undone.</p>
          <Btn danger small onClick={resetAllData}>Delete All Data</Btn>
        </div>}
      </div>}
    </div>

    {/* ── Reset Confirmation Modal ── */}
    {canWipe&&resetModal&&<div style={{position:"fixed",inset:0,background:"rgba(6,26,48,0.55)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
      <div style={{background:C.card,borderRadius:16,border:"1px solid "+C.red+"50",padding:32,maxWidth:440,width:"100%",boxShadow:"0 20px 60px rgba(11,42,74,0.16)"}}>
        <div style={{fontSize:28,marginBottom:12,textAlign:"center"}}>⚠️</div>
        <h2 style={{fontSize:18,fontWeight:700,color:C.red,margin:"0 0 8px",textAlign:"center"}}>Reset All Data?</h2>
        <p style={{fontSize:13,color:C.textMuted,margin:"0 0 20px",lineHeight:1.6,textAlign:"center"}}>This will permanently erase <strong style={{color:C.text}}>all loans, expenses, leads, campaigns, and borrower accounts stored in this browser</strong>. The app will reload to its factory defaults. This cannot be undone.</p>
        <div style={{marginBottom:16}}>
          <label style={{display:"block",fontSize:11,fontWeight:700,color:C.textDim,marginBottom:6}}>TYPE "RESET" TO CONFIRM</label>
          <Inp value={resetTyped} onChange={e=>sResetTyped(e.target.value)} placeholder="RESET" style={{textTransform:"uppercase",letterSpacing:2}}/>
        </div>
        {wipeErr&&<div style={{marginBottom:12,padding:"8px 10px",background:C.redBg,border:"1px solid "+C.red+"40",borderRadius:6,fontSize:12,color:C.red}}>{wipeErr}</div>}
        <div style={{display:"flex",gap:10}}>
          <Btn small disabled={wipeBusy} onClick={()=>{sResetModal(false);sResetTyped("");sWipeErr("");}} style={{flex:1,justifyContent:"center"}}>Cancel</Btn>
          <Btn danger small disabled={wipeBusy||resetTyped.trim().toUpperCase()!=="RESET"} onClick={runWipe} style={{flex:1,justifyContent:"center"}}>{wipeBusy?"Deleting…":"Confirm Delete"}</Btn>
        </div>
      </div>
    </div>}
  </div>);
}

// ═══════ LIMITED SETTINGS (Max) ═══════
// Two things, each saved to the signed-in person's OWN login (Supabase Auth —
// the password, and the Practice switch in user_metadata via useStaffPrefs,
// which follows him to any device; this browser's storage in offline mode).
// Nothing here writes the shared settings row. No LO split: Max's stuff
// accounts for none at all (Reece, 10/1).
// onBeforePractice: App's "push any unsaved change now" — run before Practice
// mode turns on so an edit made just before the flip is still saved. If it
// still didn't save, Practice mode only turns on after a confirm: turning it
// off later re-reads what is saved, and that edit would be gone.
function LimitedSettings({staff,onBeforePractice}){
  const who=isLimitedStaff(staff)?"max":"reece";
  const me=(STAFF&&STAFF[who])||{name:who==="max"?"Max":"Reece"};
  const [prefs,setPrefs]=useStaffPrefs(who);
  const practice=prefs.practice===true;
  const [newPw,sNewPw]=useState("");
  const [confirmPw,sConfirmPw]=useState("");
  const [pwErr,sPwErr]=useState("");
  const [pwOk,sPwOk]=useState("");
  const [pwBusy,sPwBusy]=useState(false);
  const [pracBusy,sPracBusy]=useState(false);
  const [pracErr,sPracErr]=useState("");

  const saveMyPw=async()=>{
    if(pwBusy)return;
    const pw=newPw.trim();
    sPwOk("");
    if(!pw){sPwErr("Enter a new password.");return;}
    if(newPw!==confirmPw){sPwErr("Passwords do not match.");return;}
    if(pw.length<8){sPwErr("Password must be at least 8 characters.");return;}
    if(!isSupabaseReady()){sPwErr("Local dev mode has one shared offline login — change it from Reece's Settings.");return;}
    sPwBusy(true);sPwErr("");
    try{
      const {error}=await supabase.auth.updateUser({password:pw});
      if(error){sPwErr("Password NOT changed: "+(error.message||String(error)));return;}
      sPwOk("Password changed for "+me.name+". Use the new one next time you sign in.");
      sNewPw("");sConfirmPw("");
    }catch(e){sPwErr("Password NOT changed: "+((e&&e.message)||String(e)));}
    finally{sPwBusy(false);}
  };
  const togglePractice=async()=>{
    if(pracBusy)return;
    const next=!practice;
    sPracBusy(true);sPracErr("");
    try{
      if(next&&typeof onBeforePractice==="function"){
        let flushed=true;
        try{flushed=(await onBeforePractice())!==false;}catch(e){flushed=false;}
        if(!flushed&&!window.confirm("Your latest change has NOT saved to the cloud (check your connection).\n\nTurning Practice mode on stops it from saving, and turning Practice off later re-reads what is saved — so that change would be lost.\n\nTurn on Practice mode anyway?"))return;
      }
      const r=await setPrefs({practice:next});
      if(r&&!r.ok)sPracErr(next
        ?"Practice mode is on for this session, but it couldn't be saved to your login ("+r.error+") — it won't follow you to another device."
        :"Couldn't turn Practice mode off ("+r.error+"). Check your connection and try again.");
    }finally{sPracBusy(false);}
  };

  const card={background:C.card,borderRadius:14,border:"1px solid "+C.border,padding:20};
  return(<div><Hdr t="Settings" s={"Signed in as "+me.name+" — your password and Practice mode"}/>
    <div style={{display:"flex",gap:20,flexWrap:"wrap",alignItems:"flex-start"}}>
      <div style={{flex:"1 1 380px",maxWidth:640,display:"flex",flexDirection:"column",gap:16}}>

        {/* Practice mode */}
        <div data-testid="practice-card" style={{...card,border:"1px solid "+(practice?C.orange+"70":C.border),background:practice?C.orangeBg:C.card}}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <h3 style={{fontSize:14,fontWeight:700,color:practice?C.orange:C.accentLight,margin:0,flex:1}}>🧪 Practice mode</h3>
            <button type="button" role="switch" aria-checked={practice} aria-label="Practice mode" onClick={togglePractice} disabled={pracBusy}
              style={{width:44,height:24,borderRadius:12,border:"none",padding:2,background:practice?C.orange:C.cream2,display:"inline-flex",alignItems:"center",justifyContent:practice?"flex-end":"flex-start",cursor:pracBusy?"wait":"pointer",flexShrink:0}}>
              <span style={{width:20,height:20,borderRadius:10,background:"#fff",boxShadow:"0 1px 2px rgba(6,26,48,0.3)"}}/>
            </button>
          </div>
          <p style={{fontSize:12,color:C.textMuted,margin:"8px 0 0",lineHeight:1.55}}>When it's on, nothing you do is saved anywhere — calculator runs aren't logged, expense changes last only until you turn it off or sign out, and library chats aren't kept. It follows your login to every device.</p>
          <div style={{fontSize:12,fontWeight:700,marginTop:8,color:practice?C.orange:C.textDim}}>{practice?"On — nothing is being saved.":"Off — your work saves normally."}</div>
          {pracErr&&<div style={{fontSize:12,color:C.red,marginTop:6}}>{pracErr}</div>}
        </div>

        {/* My Password */}
        <div style={{...card,border:"1px solid "+C.red+"30"}}>
          <h3 style={{fontSize:14,fontWeight:700,color:C.red,margin:"0 0 4px"}}>🔐 Change my password</h3>
          <p style={{fontSize:11,color:C.textDim,margin:"0 0 14px"}}>Changes the login for <strong style={{color:C.text}}>{me.name}</strong> (the account signed in now). Minimum 8 characters.</p>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            <div><label style={{fontSize:10,fontWeight:700,color:C.textDim,display:"block",marginBottom:4}}>NEW PASSWORD</label><Inp type="password" placeholder="New password (min 8 chars)" value={newPw} onChange={e=>{sNewPw(e.target.value);sPwOk("");}}/></div>
            <div><label style={{fontSize:10,fontWeight:700,color:C.textDim,display:"block",marginBottom:4}}>CONFIRM PASSWORD</label><Inp type="password" placeholder="Re-enter new password" value={confirmPw} onChange={e=>{sConfirmPw(e.target.value);sPwOk("");}} onKeyDown={e=>e.key==="Enter"&&saveMyPw()}/></div>
            {pwErr&&<div style={{fontSize:12,color:C.red}}>{pwErr}</div>}
            {pwOk&&<div style={{fontSize:12,color:C.green,fontWeight:600}}>{Ic.chk} {pwOk}</div>}
            <Btn primary small onClick={saveMyPw} disabled={pwBusy}>{pwBusy?"Changing…":"Change my password"}</Btn>
          </div>
        </div>
      </div>
    </div>
  </div>);
}

// Reece → the full page; a limited login (Max) → the short one.
function Settings(props){
  if(staffAccess(props.staff).settingsLimited)return <LimitedSettings staff={props.staff} onBeforePractice={props.onBeforePractice}/>;
  return <FullSettings {...props}/>;
}

export { Settings, LimitedSettings };
